const MAX_CODE_LENGTH = 16;
const CODE_REGEX = new RegExp(
  `^[A-Za-z0-9][A-Za-z0-9 _-]{3,${MAX_CODE_LENGTH - 1}}$`,
);
const DELIMITERS = [';', '\t', ','];

const HEADER_WORDS = [
  'code',
  'codes',
  'voucher',
  'vouchers',
  'vouchercode',
  'vouchercodes',
  'gutschein',
  'gutscheine',
  'gutscheincode',
  'coupon',
  'couponcode',
];

export type RejectedLine = {
  line: number;
  raw: string;
};

export type VoucherFileParseResult = {
  codes: string[];
  rejected: RejectedLine[];
  duplicatesInFile: number;
  skippedHeader: string | null;
};

const splitFields = (line: string, delimiter: string | null) => {
  const fields = delimiter === null ? [line] : line.split(delimiter);
  return fields.map((field) =>
    field
      .trim()
      .replace(/^"(.*)"$/, '$1')
      .trim(),
  );
};

/**
 * Picks the delimiter that occurs most often in the file, or null for
 * single-column files.
 */
const detectDelimiter = (content: string) => {
  let best: string | null = null;
  let bestCount = 0;
  for (const delimiter of DELIMITERS) {
    const count = content.split(delimiter).length - 1;
    if (count > bestCount) {
      best = delimiter;
      bestCount = count;
    }
  }
  return best;
};

const isHeaderField = (field: string) =>
  HEADER_WORDS.includes(field.toLowerCase().replace(/[\s_-]/g, ''));

/**
 * Reads voucher codes out of a provider file. Accepts single-column files as
 * well as `;`, tab or comma separated ones. A header line names the code column,
 * otherwise the first field of a line that looks like a code is taken. The first
 * line is only skipped when it is a header and not a code.
 */
export const parseVoucherFile = (content: string): VoucherFileParseResult => {
  const withoutBom = content.replace(/^\uFEFF/, '');
  const delimiter = detectDelimiter(withoutBom);
  const lines = withoutBom.split(/\r?\n/);

  const codes: string[] = [];
  const seen = new Set<string>();
  const rejected: RejectedLine[] = [];
  let duplicatesInFile = 0;
  let skippedHeader: string | null = null;
  let codeColumn: number | null = null;
  let isFirstContentLine = true;

  lines.forEach((rawLine, index) => {
    const fields = splitFields(rawLine, delimiter);
    if (fields.every((field) => field === '')) {
      return;
    }

    const isFirst = isFirstContentLine;
    isFirstContentLine = false;

    if (isFirst) {
      const headerColumn = fields.findIndex(isHeaderField);
      if (headerColumn !== -1) {
        skippedHeader = rawLine.trim();
        codeColumn = headerColumn;
        return;
      }
    }

    const candidates =
      codeColumn === null ? fields : [fields[codeColumn] ?? ''];
    const code = candidates.find((field) => CODE_REGEX.test(field));
    if (code === undefined) {
      if (isFirst) {
        skippedHeader = rawLine.trim();
        return;
      }
      rejected.push({ line: index + 1, raw: rawLine.trim() });
      return;
    }

    if (seen.has(code)) {
      duplicatesInFile += 1;
      return;
    }
    seen.add(code);
    codes.push(code);
  });

  return { codes, rejected, duplicatesInFile, skippedHeader };
};
